import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { Typography, colors, Icon } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import {
  deleteResultadosPdf,
  uploadResultados,
} from "services/resultadoCorrida";
import { IResultadoCorrida } from "interfaces/resultadoCorrida";

interface Props {
  onClose: () => void;
  selectedEvento: IResultadoCorrida;
  isUpadate: () => void;
}

interface FileWithPreview extends File {
  preview: string;
}

const DropzoneComponent = ({ onClose, selectedEvento, isUpadate }: Props) => {
  const [filePreview, setFilePreview] = useState<FileWithPreview[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  // list de arquivos atuais que estão salvo
  const [filesCurrent, setFilesCurrent] = useState<string[]>(
    selectedEvento?.resultado?.filenamePdf || []
  );
  // list de arquivos que serão removidos
  const [filenameRemove, setFilenameRemove] = useState<string[]>([]);

  const eventoId = selectedEvento.id;
  const resultId = selectedEvento?.resultado?.id || null;

  // Lida com o evento de arrastar e soltar arquivos, processando os arquivos aceitos.
  const onDrop = (acceptedFiles: File[]) => {
    const filesWithPreview = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    setFiles(acceptedFiles);
    setFilePreview((prevFiles) => [...prevFiles, ...filesWithPreview]);
  };

  // Remove um arquivo da lista de pré-visualizações com base no nome do arquivo.
  const removeFileDropZone = async (fileName: string) => {
    // Remove o arquivo da lista de pré-visualizações
    setFilePreview((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName)
    );
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    maxFiles: 10,
    accept: {
      "application/pdf": [".pdf"],
    },
  });

  // Envia os arquivos para o backend
  const handleEnviarArquivosResultados = async () => {
    const fileCurrentToRemoveFilter = filesCurrent.filter(
      (file) => !filenameRemove.includes(file)
    );
    // Excluir os arquivos removidos do backend
    if (filenameRemove) {
      await deleteResultadosPdf({
        resultId: resultId,
        filenameCurrent: fileCurrentToRemoveFilter,
        filenameToRemove: filenameRemove,
      });
    }

    // Enviar somente se existir um arquivo PDF
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      // Enviar os arquivos para o backend
      await uploadResultados(
        formData,
        eventoId,
        resultId,
        fileCurrentToRemoveFilter
      );
    }
    isUpadate();
    onClose();
  };

  // Excluir arquivo já envidado
  const handleExcluirArquivo = async (fileName: string) => {
    // Adicionar a lista de arquivos para serem removidos
    setFilenameRemove((prevRemove) => [...prevRemove, fileName]);
  };

  return (
    <>
      <MDBox
        {...getRootProps()}
        sx={{
          border: "2px dashed gray",
          borderRadius: "8px",
          padding: "20px",
          textAlign: "center",
          backgroundColor: colors.grey[50],
          cursor: "pointer",
          mb: 4,
        }}
      >
        <input {...getInputProps()} />
        {isDragAccept && (
          <Typography variant="subtitle1" color="green">
            Todos os arquivos serão aceitos
          </Typography>
        )}
        {isDragReject && (
          <Typography variant="subtitle1" color="error">
            Ops! Arquivo não suportado, apenas PDF
          </Typography>
        )}
        {!isDragActive && (
          <Typography variant="subtitle1" color="Highlight">
            Arraste e solte os arquivos aqui...
          </Typography>
        )}
        {/* PREVIEW DOS ARQUIVOS NO DROPZONE */}
        <MDBox display="flex" gap={1} justifyContent="center" flexWrap="wrap">
          {filePreview.length > 0 &&
            filePreview.map((file, idx) => (
              <MDBox
                key={idx}
                sx={{
                  width: 100,
                  padding: "10px",
                  display: "inline-block",
                  backgroundColor: colors.cyan[50],
                  borderRadius: "8px",
                }}
              >
                <Icon>attach_file</Icon>
                <Typography
                  variant="caption"
                  textOverflow="ellipsis"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                  }}
                >
                  {file.name}
                </Typography>
              </MDBox>
            ))}
        </MDBox>
      </MDBox>

      {/* LISTA DE ARQUIVOS PARA UPLOADS */}
      {filePreview.length > 0 && (
        <MDBox my={2}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="success" fontSize="medium">
              cloud_upload_outlined
            </Icon>
            <Typography textAlign="left" color="GrayText" variant="h6">
              Arquivos para uploads:
            </Typography>
          </MDBox>
          {filePreview.map((file) => (
            <MDBox
              key={file.name}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              sx={{ backgroundColor: colors.grey[100], padding: "10px" }}
            >
              <Typography variant="body2">{file.name}</Typography>
              <MDButton
                variant="contained"
                color="error"
                size="small"
                onClick={() => removeFileDropZone(file.name)}
              >
                Remover
              </MDButton>
            </MDBox>
          ))}
        </MDBox>
      )}

      {/* LISTA DE ARQUIVOS JÁ ENVIADOS */}
      {filesCurrent.length > 0 && (
        <MDBox mt={4}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon color="info" fontSize="medium">
              check_circle_outlined
            </Icon>
            <Typography textAlign="left" color="GrayText" variant="h6">
              Arquivos já enviados:
            </Typography>
          </MDBox>
          {filesCurrent.map((file) => (
            <MDBox
              key={file}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              sx={{
                backgroundColor: filenameRemove.includes(file)
                  ? colors.red[200]
                  : colors.grey[100],
                padding: "10px",
              }}
            >
              <Typography variant="body2">{file}</Typography>
              {!filenameRemove.includes(file) && (
                <MDButton
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => handleExcluirArquivo(file)}
                >
                  Remover
                </MDButton>
              )}
            </MDBox>
          ))}
        </MDBox>
      )}

      {/* BOTOES DE ENVIAR E CANCELAR */}
      {(filePreview.length > 0 || filenameRemove.length > 0) && (
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          my={2}
        >
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="contained"
              color="info"
              onClick={handleEnviarArquivosResultados}
            >
              Enviar
            </MDButton>
            <MDButton variant="outlined" color="info" onClick={onClose}>
              Cancelar
            </MDButton>
          </MDBox>
          {filePreview.length > 0 && (
            <Typography variant="h6">
              Total de arquivos para enviar: {filePreview.length}
            </Typography>
          )}
          {filenameRemove.length > 0 && (
            <Typography variant="h6">
              Total de arquivos para remover: {filenameRemove.length}
            </Typography>
          )}
        </MDBox>
      )}
    </>
  );
};

export default DropzoneComponent;
